import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Divider,
  Chip,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import theme from '../theme'
import {
  Flight,
  FlightFrequency,
  FlightImpressions,
  DeliveryType,
  CreativePreviewResponse,
  CampaignStatus,
  ContentCategory,
  ErrorResponse,
  CloneFlightInput,
  FlightTargeting,
  CloneFlightInputSchema
} from '../../types/campaign'
import {
  OUTDATED_CREATIVE_WARNING,
  MISSING_CREATIVE_WARNING,
  TARGETING_UNINSTALLED_STORES_WARNING
} from '../../types/constants'
import CalendarStart from '../icons/CalendarStart'
import CalendarEnd from '../icons/CalendarEnd'
import DollarBill from '../icons/DollarBill'
import TimerIcon from '../icons/TimerIcon'
import ImpressionsIcon from '../icons/ImpressionsIcon'
import CopyIcon from '../icons/CopyIcon'
import InfoDisplay from '../InfoDisplay'
import FlightToolbar from '../FlightToolbar'
import FlightCreative from '../FlightCreative'
import { formatDate, formatCurrency, formatNumber, pacificDateStringToUtcStartOfDay, pacificDateStringToUtcEndOfDay } from '../utils'
import { updateCreativePreview, cloneFlight, scheduleFlight } from '../getData'
import StatusChip from '../StatusChip'
import AppModal from '../AppModal'
import FlightDates from '../FlightDates'

dayjs.extend(utc)
dayjs.extend(timezone)

interface Props {
  campaignId: number
  campaignStatus: CampaignStatus
  flight: Flight
  openDeleteModal: (flightId: number) => void
  refetchCampaign: () => void
  campaignContentCategory?: ContentCategory | null
  isGtvUser: boolean
}

const FlightAccordion = ({
  campaignId,
  campaignStatus,
  campaignContentCategory,
  flight,
  openDeleteModal,
  refetchCampaign,
  isGtvUser
}: Props) => {
  const navigate = useNavigate()
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [showAllStores, setShowAllStores] = useState(false)
  const [creativePreview, setCreativePreview] =
    useState<CreativePreviewResponse | null>(null)
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: '' })
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false)
  const [showAllUninstalledStores, setShowAllUninstalledStores] = useState(false)
  const [copied, setCopied] = useState(false)

  const duplicateFormMethods = useForm<CloneFlightInput>({
    mode: 'onSubmit',
    resolver: yupResolver(CloneFlightInputSchema),
    defaultValues: {
      duration: {
        starts_at: '',
        ends_at: ''
      }
    }
  })

  const {
    mutate: updateCreativePreviewMutation,
    isPending: isUpdatingPreview,
    isError: isErrorUpdatingPreview
  } = useMutation({
    mutationFn: updateCreativePreview,
    onSuccess: (data) => {
      setCreativePreview(data)
    },
    onError: (error) => {
      console.error(error)
    }
  })

  const {
    mutate: cloneFlightMutation,
    isPending: isDuplicating,
  } = useMutation({
    mutationFn: () => {
      const formValues = duplicateFormMethods.getValues()
      const duration = {
        starts_at: formValues.duration.starts_at
          ? pacificDateStringToUtcStartOfDay(formValues.duration.starts_at)
          : '',
        ends_at: formValues.duration.ends_at
          ? pacificDateStringToUtcEndOfDay(formValues.duration.ends_at)
          : ''
      }
      return cloneFlight(flight.id, campaignId, duration)
    },
    onSuccess: (data: Flight) => {
      setOpenDuplicateModal(false)
      navigate(`/campaign/${campaignId}/flight/${data.id}`)
    },
    onError: (error: AxiosError) => {
      console.error(error)
      setOpenDuplicateModal(false)
      const response = error.response?.data as ErrorResponse
      setOpenSnackbar({
        open: true,
        message: response.context?.[0] || 'Error duplicating flight'
      })
    }
  })

  const {
    mutate: scheduleFlightMutation,
    isPending: isPendingStatusUpdate
  } = useMutation({
    mutationFn: (status: string) => scheduleFlight(flight.id, status),
    onSuccess: () => {
      refetchCampaign()
      setOpenSnackbar({
        open: true,
        message: 'Flight status updated successfully'
      })
    },
    onError: (error: AxiosError) => {
      console.error(error)
      const response = error.response?.data as ErrorResponse
      setOpenSnackbar({
        open: true,
        message: response?.context?.[0] || 'Failed to update flight status'
      })
    }
  })

  const handleDuplicateFlight = duplicateFormMethods.handleSubmit(() => {
    cloneFlightMutation()
  })

  useEffect(() => {
    // Only fetch preview for this flight when its accordion is expanded
    if (!expandedIds.includes(flight.id)) return

    if (
      flight.targeting.stores.length > 0 ||
      flight.targeting.placements.length > 0 ||
      flight.targeting.zones.length > 0
    ) {
      const targetingInput = {
        stores: flight.targeting.stores.map((store) => ({ id: store.id })),
        placements: flight.targeting.placements.map((placement) => ({
          id: placement.id
        })),
        zones: flight.targeting.zones.map((zone) => ({ id: zone.id }))
      }
      updateCreativePreviewMutation(targetingInput)
    }
  }, [flight.targeting, expandedIds])

  const getCreativeOrientation = (creative: {
    assets: Array<{ type: string; aspect_ratio: { orientation: string } }>
  }) => {
    const asset =
      creative.assets.find((creativeAsset) => creativeAsset.type === 'video') ?? creative.assets[0]
    return asset?.aspect_ratio.orientation
  }

  const allFormatsUploaded = creativePreview
    ? creativePreview.aspect_ratios.every((aspectRatio) => {
        return flight.creatives.some((creative) => {
          const orientation = getCreativeOrientation(creative)
          return orientation === aspectRatio.orientation
        })
      })
    : true

  const hasOutdatedFormats = creativePreview
    ? flight.creatives.some((creative) => {
        const orientation = getCreativeOrientation(creative)
        if (!orientation) return false
        return !creativePreview.aspect_ratios.some(
          (aspectRatio) => orientation === aspectRatio.orientation
        )
      })
    : false

  const targetingUninstalledStores = flight.targeting.stores.length > 0
    ? flight.targeting.stores.filter(store => !store.installed)
    : []

  const targetedStores = flight.targeting.stores.length > 0
    ? flight.targeting.stores.filter(store => store.installed)
    : []

  const handleUninstalledStoresCopy = async (values: FlightTargeting['stores']) => {
    try {
      if (!values?.length) return

      const text = values
        .map((store) => `${store.banner.name} - ${store.name}`)
        .join('\n')
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 300)
    } catch (err) {
      console.error('Failed to copy', err)
    }
  }

  let warningMessage = undefined
  let targetingWarningMessage = undefined
  if (hasOutdatedFormats) {
    warningMessage = OUTDATED_CREATIVE_WARNING
  }
  if (!allFormatsUploaded) {
    warningMessage = MISSING_CREATIVE_WARNING
  }
  if (targetingUninstalledStores.length > 0) {
    targetingWarningMessage = TARGETING_UNINSTALLED_STORES_WARNING
  }

  const formatAlert = (
    <Alert
      severity="warning"
      sx={{
        alignItems:'top',
        marginBottom: theme.space['24'],
        borderRadius: theme.borders['rounded-10'],
        backgroundColor: theme.palette['orange-200'],
        color: theme.palette['orange-900']
    }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'top' }}>
        <Typography variant="body-8" fontWeight="bold" color={theme.palette['orange-900']} display='flex'>
          {targetingUninstalledStores?.length} stores are no longer installed
          <CopyIcon
            onClick={() => handleUninstalledStoresCopy(targetingUninstalledStores)}
            sx={{
              paddingLeft: theme.space['4'],
              cursor: 'pointer',
              transition: 'transform 0.2s ease, font-weight 0.2s ease, color 0.2s ease',
              transform: copied ? 'scale(1.3)' : 'scale(1)',
              color: copied ? theme.palette['green-900'] : 'inherit'
          }}/>
        </Typography>
        <Typography variant="body-8" color={theme.palette['orange-900']} sx={{ marginTop: theme.space['4'] }}>
          {targetingWarningMessage}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            marginTop: theme.space['8'],
            flexDirection: 'row',
            gap: theme.space['12'],
            flexWrap: 'wrap'
          }}
        >
          {targetingUninstalledStores .slice(0, showAllUninstalledStores ? undefined : 5)
            .map((store) => (
              <Chip key={store.id} label={`${store.banner.name} - ${store.name}`} />
            ))}
          {targetingUninstalledStores.length > 5 && (
            <Chip
              sx={{
                backgroundColor: theme.palette['sage-100'],
                color: theme.palette['sage-900'],
                fontSize: theme.typography['body-2'].fontSize,
                fontWeight: '800'
            }}
              label={
                showAllUninstalledStores
                  ? 'SHOW LESS'
                  : `${targetingUninstalledStores.length - 5} MORE`
              }
              onClick={() => setShowAllUninstalledStores(!showAllUninstalledStores)} clickable
            />
          )}
        </Box>
      </Box>
    </Alert>
  )

  const startTime = `${formatDate(
    flight.duration.starts_at,
    flight.time_zone
  )}`
  const endTime = `${formatDate(
    flight.duration.ends_at,
    flight.time_zone
  )}`
  const flightStatus: CampaignStatus = flight.status || CampaignStatus.Draft

  const canUpdateFlightStatus =
    flightStatus === CampaignStatus.Draft &&
    campaignStatus !== CampaignStatus.Draft &&
    campaignStatus !== CampaignStatus.Scheduled

  const canDelete = ![
    CampaignStatus.Live,
    CampaignStatus.Dark,
    CampaignStatus.Completed
  ].includes(flightStatus)

  return (
    <>
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <AppModal
        open={openDuplicateModal}
        onClose={() => setOpenDuplicateModal(false)}
        header={
          <Box>
            <Typography variant="heading-4">Duplicate Flight</Typography>
          </Box>
        }
        content={
          isDuplicating ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px'
              }}
            >
              <CircularProgress sx={{ color: theme.palette['accent-500'] }} />
            </Box>
          ) : (
            <Box sx={{ marginTop: theme.space['24'] }}>
              <Typography variant="body-12" sx={{ marginBottom: theme.space['24'] }}>
                Set the start and end dates for the duplicated flight.
              </Typography>
              <FormProvider {...duplicateFormMethods}>
                <Box sx={{
                  '& > div': {
                    padding: 0,
                    marginTop: theme.space['8'],
                    '& > .MuiTypography-root:first-of-type': { display: 'none' }
                  }
                }}>
                  <FlightDates context="flight" />
                </Box>
              </FormProvider>
            </Box>
          )
        }
        footer={
          !isDuplicating && (
            <Box display="flex" gap={theme.space['16']}>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  width: '264px',
                  height: '56px',
                  borderRadius: theme.borders['rounded-10']
                }}
                onClick={() => setOpenDuplicateModal(false)}
              >
                <Typography variant="ui-3">Cancel</Typography>
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  width: '264px',
                  height: '56px',
                  borderRadius: theme.borders['rounded-10']
                }}
                onClick={handleDuplicateFlight}
              >
                <Typography variant="ui-3">Duplicate</Typography>
              </Button>
            </Box>
          )
        }
      />
      <Accordion
        key={flight.id}
      sx={{
        boxShadow: 'none',
        padding: theme.space['16'],
        borderRadius: theme.borders['rounded-18'],
        '&.Mui-expanded': {
          margin: '0px'
        },
        '&:first-of-type': {
          borderRadius: theme.borders['rounded-18']
        },
        '&:last-of-type': {
          borderRadius: theme.borders['rounded-18']
        },
        '&:before': {
          opacity: 0
        }
      }}
      expanded={expandedIds.includes(flight.id)}
      onChange={(_, expanded) =>
        setExpandedIds(
          expanded
            ? [...expandedIds, flight.id]
            : expandedIds.filter((id) => id !== flight.id)
        )
      }
    >
      <AccordionSummary sx={{ width: '100%' }}>
        <Typography
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space['8'],
            }}
          >
            <FlightToolbar
              onEdit={() =>
                navigate(`/campaign/${campaignId}/flight/${flight.id}`)
              }
              onDuplicate={() => {
                setOpenDuplicateModal(true)
              }}
              onDelete={() => openDeleteModal(flight.id)}
              isOpen={expandedIds.includes(flight.id)}
              canDelete={!canDelete}
            />
            <Typography
              variant="heading-4"
              noWrap
              sx={{
                maxWidth: {
                  xs: '300px',
                  sm: '500px',
                  md: '700px',
                  lg: '900px',
                  xl: '1100px'
                },
              }}
              >
              {flight.name}
            </Typography>
            <Typography
              variant="heading-1"
              color={theme.palette['sage-500']}
              >
              {flight.description}
            </Typography>
          </Box>
          {targetingUninstalledStores.length > 0 && !expandedIds.includes(flight.id) && (
            <Alert
              severity="warning"
              sx={{
                alignItems:'top',
                maxWidth:'max-content',
                padding: theme.space['8'],
                marginBottom: theme.space['4'],
                borderRadius: theme.borders['rounded-10'],
                backgroundColor: theme.palette['orange-200'],
                color: theme.palette['orange-900']
              }}
            >
              <Typography variant="body-8" fontWeight="bold" color={theme.palette['orange-900']} display='flex'>
                {targetingUninstalledStores?.length} stores are no longer installed
              </Typography>
            </Alert>
          )}
          {!expandedIds.includes(flight.id) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: theme.space['24'],
                marginTop: theme.space['32'],
              }}
            >
              <InfoDisplay
                label="Start"
                value={startTime}
                color={theme.palette['base-black']}
                icon={<CalendarStart sx={{ width: '17px', height: '19px' }} />}
                flex={0}
              />
              <InfoDisplay
                label="End"
                value={endTime}
                color={theme.palette['base-black']}
                icon={<CalendarEnd sx={{ width: '17px', height: '19px' }} />}
                flex={0}
              />
              <InfoDisplay
                label="Status"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StatusChip
                      status={flightStatus}
                      onStatusChange={
                        canUpdateFlightStatus ? scheduleFlightMutation : () => null
                      }
                      isUpdating={!canUpdateFlightStatus || isPendingStatusUpdate}
                    />
                  </Box>
                }
                flex={0}
              />
              <InfoDisplay
                label="Budget"
                value={formatCurrency(flight.delivery.budget)}
                color={theme.palette['base-black']}
                icon={<DollarBill sx={{ width: '17px', height: '18px' }} />}
                flex={0}
              />
            </Box>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          '&.MuiAccordionDetails-root': {
            width: '100%',
          }
        }}
      >
        {/* Details section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            },
            marginTop: theme.space['32'],
            marginBottom: theme.space['28'],
            gap: theme.space['16'],
          }}
        >
          <InfoDisplay
            label="Start"
            value={startTime}
            color={theme.palette['base-black']}
            icon={<CalendarStart sx={{ width: '17px', height: '19px' }} />}
            flex={1.3}
          />
          <InfoDisplay
            label="End"
            value={endTime}
            color={theme.palette['base-black']}
            icon={<CalendarEnd sx={{ width: '17px', height: '19px' }} />}
            flex={1.3}
          />
          <InfoDisplay
            label="Status"
            value={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusChip
                  status={flightStatus}
                  onStatusChange={
                    canUpdateFlightStatus ? scheduleFlightMutation : () => null
                  }
                  isUpdating={!canUpdateFlightStatus || isPendingStatusUpdate}
                />
              </Box>
            }
            flex={0.8}
          />
          <Box sx={{ flex: 0.5 }}></Box>
        </Box>
        <Divider />
        {/* Spend section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            },
            marginTop: theme.space['28'],
            marginBottom: theme.space['28'],
            gap: theme.space['16'],
          }}
        >
          <InfoDisplay
            label="Budget"
            value={formatCurrency(flight.delivery.budget)}
            icon={<DollarBill sx={{ width: '17px', height: '22px' }} />}
          />
          <InfoDisplay
            label={
              flight.delivery.type === DeliveryType.Impressions
                ? 'Impressions'
                : 'Play Frequency'
            }
            value={formatNumber(
              flight.delivery.type === DeliveryType.Impressions
                ? (flight.delivery as FlightImpressions).impression_target
                : (flight.delivery as FlightFrequency).play_frequency_seconds
            )}
            icon={<ImpressionsIcon sx={{ width: '17px', height: '22px' }} />}
          />
          <Box
            sx={{
              flex: 1
            }}
          >
            {flight.delivery.type === DeliveryType.Impressions && (
              <InfoDisplay
                label="CPM"
                value={formatCurrency(
                  (flight.delivery as FlightImpressions).cpm
                )}
                icon={<TimerIcon sx={{ width: '17px', height: '22px' }} />}
              />
            )}
          </Box>
        </Box>
        <Divider />
        {/* Targeting section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.space['8'],
            marginTop: theme.space['28'],
            marginBottom: theme.space['28'],
          }}
        >
          <Typography
            variant="body-10"
            color={theme.palette['sage-400']}
            sx={{ flex: 0.165 }}
          >
            Targeting
          </Typography>
          <Button
            color="secondary"
            variant="contained"
            onClick={() =>
              navigate(`/campaign/${campaignId}/flight/${flight.id}/targets`)
            }
            title="Edit Targeting"
          >
            <Typography variant="ui-1" color={theme.palette['sage-900']}>
              Edit
            </Typography>
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: theme.space['28'],
            marginBottom: theme.space['28']
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space['12'],
                marginBottom: theme.space['20']
              }}
            >
              {targetingWarningMessage && formatAlert}
              <Typography
                variant="body-8"
                sx={{ color: theme.palette['sage-400'] }}
              >
                Stores ({targetedStores.length})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.space['12'],
                  flexWrap: 'wrap'
                }}
              >
                {targetedStores
                  .slice(0, showAllStores ? undefined : 9)
                  .map((store) => (
                    <Chip key={store.id} label={`${store.banner.name} - ${store.name}`} />
                  ))}
                {targetedStores.length > 9 && (
                  <Chip
                    sx={{
                      backgroundColor: theme.palette['sage-100'],
                      color: theme.palette['sage-900'],
                      fontSize: theme.typography['body-2'].fontSize,
                      fontWeight: '800'
                    }}
                    label={
                      showAllStores
                        ? 'SHOW LESS'
                        : `${targetedStores.length - 9} MORE`
                    }
                    onClick={() => setShowAllStores(!showAllStores)}
                    clickable
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space['12'],
                marginBottom: theme.space['20']
              }}
            >
              <Typography
                variant="body-8"
                sx={{ color: theme.palette['sage-400'] }}
              >
                Zones ({flight.targeting.zones.length})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.space['12'],
                  flexWrap: 'wrap'
                }}
              >
                {flight.targeting.zones.map((zone) => (
                  <Chip key={zone.id} label={zone.name} />
                ))}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space['12']
              }}
            >
              <Typography
                variant="body-8"
                sx={{ color: theme.palette['sage-400'] }}
              >
                Placements ({flight.targeting.placements.length})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.space['12'],
                  flexWrap: 'wrap'
                }}
              >
                {flight.targeting.placements.map((placement) => (
                  <Chip key={placement.id} label={placement.name} />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        <Divider />
        {/* Creative section   */}
        {flight.creatives.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              marginTop: theme.space['28'],
              marginBottom: theme.space['28']
            }}
          >
            <FlightCreative
              creativeFormats={creativePreview}
              creativeAssets={flight.creatives}
              isPending={isUpdatingPreview}
              isError={isErrorUpdatingPreview}
              flight={flight}
              isFlightAccordion={true}
              saveTargetsMutation={refetchCampaign}
              warningMessage={warningMessage}
              campaignStatus={flight.status}
              campaignContentCategory={campaignContentCategory}
              isGtvUser={isGtvUser}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
    </>
  )
}

export default FlightAccordion
